/**
 * 閒置歸零 — 對應 docs/Kiosk_UI_Flow.md 設計原則「30 秒閒置歸零」。
 *
 * 任何畫面 30 秒無互動 → 清除購票草稿（個資）並回 Attract。
 * 例外：付款進行中（idleSuspended=true）不歸零，對應 Flow 3.11。
 * 已在 Attract（'/'）或維運機台設定頁（'/device-setup'）則不歸零——後者非顧客
 * 動線、無個資草稿，且維運設定時不應被閒置踢回。e2e / 展示可用 URL `?idle-ms=N` 縮短。
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useKiosk } from '../lib/session';

const DEFAULT_IDLE_MS = 30_000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart'] as const;

function resolveIdleMs(): number {
  const raw = new URLSearchParams(window.location.search).get('idle-ms');
  if (raw !== null) {
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1000 && n <= 120_000) return n;
  }
  return DEFAULT_IDLE_MS;
}

export function IdleReset(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const { idleSuspended, resetSession } = useKiosk();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Attract 頁與維運設定頁不需歸零；付款進行中暫停。
    if (location.pathname === '/' || location.pathname === '/device-setup' || idleSuspended) {
      return undefined;
    }
    const idleMs = resolveIdleMs();

    const onTimeout = (): void => {
      resetSession();
      navigate('/', { replace: true });
    };
    const reset = (): void => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(onTimeout, idleMs);
    };

    reset();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [location.pathname, idleSuspended, navigate, resetSession]);

  return null;
}
